import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { settings } from '../lib/store';

const COLORS = ['#c96442', '#e8a35c', '#f2d27a', '#7f9f5c', '#6e9fc4', '#fbf7ee', '#b5573a'];

interface Bit { x: number; y: number; vx: number; vy: number; rot: number; vr: number; w: number; h: number; color: string; round: boolean }

/** Bursts confetti from both bottom corners whenever `fire` changes to a new truthy value. */
export function Confetti({ fire }: { fire: number | string | false | null | undefined }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!fire) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!settings.get().animations || reduced) return;
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      canvas.width = innerWidth * dpr;
      canvas.height = innerHeight * dpr;
    };
    resize();
    window.addEventListener('resize', resize);

    const bits: Bit[] = [];
    const burst = (fromLeft: boolean) => {
      for (let i = 0; i < 90; i++) {
        const angle = (fromLeft ? -60 : -120) * (Math.PI / 180) + (Math.random() - 0.5) * 0.9;
        const speed = 9 + Math.random() * 9;
        bits.push({
          x: fromLeft ? 0 : innerWidth, y: innerHeight * 0.85,
          vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
          rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.35,
          w: 6 + Math.random() * 6, h: 8 + Math.random() * 8,
          color: COLORS[(Math.random() * COLORS.length) | 0], round: Math.random() < 0.2,
        });
      }
    };
    burst(true);
    burst(false);
    const second = setTimeout(() => { burst(true); burst(false); }, 350);

    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = now - start;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      for (const b of bits) {
        b.vy += 0.32;          // gravity
        b.vx *= 0.985;         // air drag
        b.vy *= 0.985;
        b.x += b.vx + Math.sin((t + b.w * 100) / 260) * 0.6; // flutter
        b.y += b.vy;
        b.rot += b.vr;
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.rot);
        ctx.globalAlpha = Math.max(0, Math.min(1, (4200 - t) / 800));
        ctx.fillStyle = b.color;
        if (b.round) { ctx.beginPath(); ctx.arc(0, 0, b.w / 2, 0, Math.PI * 2); ctx.fill(); }
        else ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h * Math.abs(Math.cos(b.rot * 2)) + 1);
        ctx.restore();
      }
      if (t < 4200) frame = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, innerWidth, innerHeight);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(second);
      window.removeEventListener('resize', resize);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [fire]);

  // In body, so an animated (transformed) page cannot clip or offset it.
  return createPortal(<canvas ref={ref} className="confetti" aria-hidden="true" />, document.body);
}
